import { Link } from "react-router-dom";
import { BRAND, BrandMarquee } from "@/constants/brand";
import { useAuth } from "@/context/AuthContext";
import { ArrowDown, ArrowRight, Check } from "lucide-react";

/** All editable Founders-page copy lives here. */
export const FOUNDERS = {
  photo: "/brand/founders-alvin-moana.jpg",
  names: "Alvin & Moana Ahquin",
  teachables: [
    { title: "Product", body: "Learn the Smash Lemonade preparation process, recipes, portions, presentation, and quality standards." },
    { title: "Setup", body: "Learn how to build and organize an efficient professional pop-up operation." },
    { title: "Equipment", body: "Learn how to properly use the Smash equipment, slicers, smashers, tables, tents, and supporting equipment." },
    { title: "Events", body: "Learn how to identify events, communicate with organizers, evaluate opportunities, and prepare for different crowd sizes." },
    { title: "Inventory", body: "Learn how to estimate lemons, ice, cups, syrups, water, and supplies based on expected sales." },
    { title: "Operations", body: "Learn opening procedures, service flow, customer experience, cleanup, and closing procedures." },
    { title: "Brand", body: "Learn how to represent Smash Lemonade professionally while maintaining the personality and culture of the brand." },
  ],
  mindsetWords: ["Resilient", "Resourceful", "Coachable", "Consistent"],
  standard: [
    ["We show up.", "Even when it's inconvenient."],
    ["We figure things out.", "Problems are part of business."],
    ["We protect the brand.", "Every cup and every interaction represents Smash Lemonade."],
    ["We serve people.", "Customers and communities come before shortcuts."],
    ["We stay coachable.", "There is always something to improve."],
    ["We adapt.", "Weather changes. Events change. Markets change. We adjust."],
    ["We create opportunities.", "We don't sit around waiting for someone else to change our circumstances."],
  ],
};

const Section = ({ children, className = "", testId }) => (
  <section data-testid={testId} className={`mx-auto max-w-6xl px-5 py-20 md:px-10 md:py-28 ${className}`}>
    {children}
  </section>
);

const Eyebrow = ({ children }) => (
  <div className="mono text-[11px] tracking-[0.35em] text-primary">{children}</div>
);

export default function Founders() {
  const { user } = useAuth();

  return (
    <div data-testid="founders-page">
      {/* top bar */}
      <div className="sticky top-0 z-20 border-b border-border bg-[#0A0A0A]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 md:px-10">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Smash Lemonade" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-4 md:gap-5">
            <span className="mono hidden text-[11px] tracking-widest text-primary sm:inline">FOUNDERS</span>
            <Link
              to="/start"
              data-testid="founders-nav-pricing"
              className="mono text-[10px] tracking-widest text-muted-foreground hover:text-primary transition-colors md:text-[11px]"
            >
              PRICING
            </Link>
            <Link
              to={user ? "/academy" : "/login"}
              data-testid="founders-nav-academy"
              className="mono text-[10px] tracking-widest text-muted-foreground hover:text-primary transition-colors md:text-[11px]"
            >
              {user ? "ACADEMY →" : "OPERATOR LOGIN →"}
            </Link>
          </div>
        </div>
      </div>

      {/* SECTION 1 — HERO */}
      <div className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0"
          style={{ backgroundImage: `url(${BRAND.photos.tribal})`, backgroundSize: "420px", opacity: 0.3 }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/70 via-[#0A0A0A]/85 to-[#0A0A0A]" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 py-20 md:grid-cols-2 md:px-10 md:py-28">
          <div className="fade-up">
            <Eyebrow>SMASH LEMONADE · SINCE THE FARMERS MARKET</Eyebrow>
            <h1 className="neon mt-4 text-4xl leading-[0.95] sm:text-5xl lg:text-6xl">
              We didn't just make lemonade.
              <br />
              <span className="text-primary">We smashed our lemons.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-muted-foreground md:text-lg">
              Smash Lemonade was built on a simple philosophy: life's hardest moments can become
              your greatest opportunities.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#our-story"
                data-testid="founders-cta-story"
                className="display group inline-flex items-center gap-2 rounded-sm bg-primary px-6 py-3 text-xl text-primary-foreground transition-colors hover:bg-[#E6C800]"
              >
                Our Story
                <ArrowDown size={18} className="transition-transform group-hover:translate-y-0.5" />
              </a>
              <span className="mono text-[11px] tracking-widest text-muted-foreground">
                FOUNDED BY {FOUNDERS.names.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="fade-up">
            <div className="relative border border-border bg-[#141414] p-2">
              <img
                src={FOUNDERS.photo}
                alt={FOUNDERS.names}
                data-testid="founders-photo"
                className="h-full w-full object-cover"
              />
              <div className="mono absolute bottom-4 left-4 bg-[#0A0A0A]/85 px-2 py-1 text-[10px] tracking-widest text-primary">
                {FOUNDERS.names.toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BrandMarquee />

      {/* SECTION 2 — MEET THE FOUNDERS */}
      <Section testId="founders-story">
        <span id="our-story" className="block -translate-y-24" />
        <Eyebrow>SECTION 01</Eyebrow>
        <h2 className="mt-3 text-4xl sm:text-5xl">Meet the Founders</h2>
        <p className="brand-script mt-2 text-3xl text-primary">{FOUNDERS.names}</p>
        <div className="mt-8 grid gap-8 md:grid-cols-2">
          <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Smash Lemonade wasn't created in a boardroom or developed by a large restaurant
              company. It started as a family business.
            </p>
            <p>
              After relocating from the Bay Area to Utah and navigating major changes in life, Alvin
              and Moana began looking for ways to create new opportunities for their family. What
              began with selling fresh-smashed lemonade at local farmers markets grew into something
              much bigger.
            </p>
            <p className="text-white">They learned the business by doing it.</p>
          </div>
          <ul className="mono grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {[
              "Setting up tents.",
              "Cutting lemons.",
              "Hauling ice.",
              "Finding events.",
              "Dealing with weather.",
              "Learning permits.",
              "Serving long lines.",
              "Making mistakes.",
              "Improving systems.",
              "Building relationships with cities and organizers.",
              "And showing up again the next weekend.",
            ].map((line) => (
              <li key={line} className="border-l border-border pl-3">
                {line}
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-10 border-l-2 border-primary pl-5 text-lg text-white md:text-xl">
          Smash Lemonade grew because the founders learned to take whatever was in front of them and
          find the opportunity inside it. That became the foundation of the brand.
        </p>
      </Section>

      {/* SECTION 3 — THE SMASH PHILOSOPHY */}
      <div className="relative overflow-hidden border-y border-border">
        <div
          className="absolute inset-0 photo-band opacity-25"
          style={{ backgroundImage: `url(${BRAND.photos.graffitiTags})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        <Section testId="founders-philosophy" className="relative">
          <Eyebrow>THE SMASH PHILOSOPHY</Eyebrow>
          <h2 className="neon mt-3 text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
            Life gives everyone lemons.
            <br />
            <span className="text-primary">What you do with them is up to you.</span>
          </h2>
          <div className="mt-10 grid gap-8 md:grid-cols-2">
            <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                The name Smash Lemonade represents more than the way we make our drinks. It
                represents how we believe people can approach life.
              </p>
              <p>
                A lemon can represent a setback. A rejection. A failed event. A slow weekend. A
                financial challenge. A closed door. Or simply a moment when life doesn't go
                according to plan.
              </p>
            </div>
            <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
              <p className="text-white">The Smash mentality asks a different question:</p>
              <p className="brand-script text-3xl text-primary">"What can I create from this?"</p>
              <p>
                Instead of allowing adversity to define the outcome, we look for the opportunity
                hidden inside it. We smash the lemon. We learn. We adapt. We keep moving. And we
                turn what we have into something valuable.
              </p>
            </div>
          </div>
          <blockquote className="mt-14 border border-primary/50 bg-[#191603] p-8 md:p-12">
            <p className="display neon text-3xl leading-tight sm:text-4xl md:text-5xl">
              The lemons aren't the story.
              <br />
              <span className="text-primary">What you build from them is.</span>
            </p>
          </blockquote>
        </Section>
      </div>

      {/* SECTION 4 — FROM MINDSET TO BUSINESS */}
      <Section testId="founders-academy-bridge">
        <Eyebrow>FROM MINDSET TO BUSINESS</Eyebrow>
        <h2 className="mt-3 text-4xl sm:text-5xl">This Is Where Smash Academy Begins</h2>
        <div className="mt-8 grid gap-8 md:grid-cols-2">
          <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>Running a Smash Lemonade pop-up looks simple from the outside.</p>
            <p className="mono text-white">Tent. Lemons. Ice. Cups. Customers.</p>
            <p>
              But the real business happens behind the scenes. Successful operators have to find
              opportunities, communicate with event organizers, prepare inventory, manage costs,
              work through long days, adapt when things go wrong, serve customers well, and come
              back ready to do it again.
            </p>
          </div>
          <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              That's why Smash Academy isn't only about recipes and equipment.{" "}
              <span className="text-white">We're developing operators.</span>
            </p>
            <p>People capable of taking responsibility for their own opportunity.</p>
            <p>
              People who can walk into a farmers market, festival, sporting event, school event,
              corporate event, or community gathering and confidently operate their own Smash
              Lemonade pop-up.
            </p>
          </div>
        </div>
      </Section>

      {/* SECTION 5 — WHAT WE CAN TEACH YOU */}
      <div className="border-y border-border bg-[#0d0d0d]">
        <Section testId="founders-teachables">
          <Eyebrow>THE CURRICULUM</Eyebrow>
          <h2 className="mt-3 text-4xl sm:text-5xl">
            We can teach you <span className="text-primary">the business.</span>
          </h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FOUNDERS.teachables.map((t) => (
              <div
                key={t.title}
                data-testid={`teachable-${t.title.toLowerCase()}`}
                className="border border-border bg-[#141414] p-6 transition-all hover:-translate-y-1 hover:border-primary"
              >
                <Check size={16} className="text-primary" />
                <div className="display mt-3 text-2xl">{t.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{t.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 text-center text-lg text-white md:text-2xl">
            But there is one thing we can't teach for you.
          </p>
        </Section>
      </div>

      {/* SECTION 6 — THE OPERATOR MINDSET */}
      <div className="relative overflow-hidden border-b border-border">
        <div
          className="absolute inset-0 photo-band opacity-20"
          style={{ backgroundImage: `url(${BRAND.photos.lemonPurple})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/88" />
        <Section testId="founders-mindset" className="relative">
          <Eyebrow>THE OPERATOR MINDSET</Eyebrow>
          <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl lg:text-6xl">
            You have to bring <span className="text-primary">the mindset.</span>
          </h2>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div className="space-y-3 text-base leading-relaxed text-muted-foreground">
              <p>We can give you tools. We can give you systems. We can teach you recipes. We can show you what we've learned.</p>
              <p className="text-white">
                But nobody can give you persistence. Nobody can make you show up. Nobody can force
                you to solve problems. And nobody can guarantee that every event will be successful.
              </p>
            </div>
            <div className="space-y-3 text-base leading-relaxed text-muted-foreground">
              <p>Entrepreneurship requires the ability to encounter a problem and say:</p>
              <p className="brand-script text-3xl text-primary">"Alright. What do I do next?"</p>
              <p className="text-white">That's the Smash mentality.</p>
            </div>
          </div>
          <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FOUNDERS.mindsetWords.map((w) => (
              <div
                key={w}
                data-testid={`mindset-${w.toLowerCase()}`}
                className="display border border-border bg-[#141414] px-3 py-8 text-center text-2xl text-primary sm:text-xl lg:text-2xl xl:text-3xl"
              >
                {w}
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-base leading-relaxed text-muted-foreground">
            You don't need to know everything before you start. You don't need to be a lifelong
            entrepreneur. You don't need to have a perfect background. But you do need to be willing
            to learn, work, adapt, and keep moving when things don't go according to plan.
          </p>
        </Section>
      </div>

      {/* SECTION 7 — THE SMASH OPERATOR STANDARD */}
      <Section testId="founders-standard">
        <Eyebrow>THE SMASH OPERATOR STANDARD</Eyebrow>
        <h2 className="mt-3 text-4xl leading-tight sm:text-5xl">
          A Smash Operator doesn't wait for opportunity.
          <br />
          <span className="text-primary">They learn how to create it.</span>
        </h2>
        <div className="mt-10 divide-y divide-border border-y border-border">
          {FOUNDERS.standard.map(([headline, sub], i) => (
            <div key={headline} className="flex items-baseline gap-5 py-5">
              <span className="display text-2xl text-primary/60">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <div className="display text-2xl">{headline}</div>
                <p className="text-sm text-muted-foreground">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* SECTION 8 — A MESSAGE FROM THE FOUNDERS */}
      <div className="border-y border-border bg-[#0d0d0d]">
        <Section testId="founders-letter">
          <Eyebrow>A MESSAGE FROM US</Eyebrow>
          <div className="mt-6 grid gap-10 md:grid-cols-[auto_1fr] md:items-start">
            <img
              src={FOUNDERS.photo}
              alt={FOUNDERS.names}
              className="h-40 w-40 border border-border object-cover md:h-56 md:w-56"
            />
            <div className="space-y-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              <p>
                "When we started Smash Lemonade, we didn't have everything figured out. We learned
                by showing up, making mistakes, asking questions, solving problems, and continuing
                to move forward.
              </p>
              <p>
                That's exactly why Smash Academy exists. We want to take everything we've learned
                and create a path for other people who want to build something of their own.
              </p>
              <p className="text-white">
                But we also want you to understand something before you begin. Owning a business
                isn't about avoiding problems. It's about becoming the kind of person who can solve
                them.
              </p>
              <p>Life is going to hand all of us lemons. Our philosophy is simple:</p>
              <p className="brand-script text-2xl text-primary md:text-3xl">
                Smash them. Turn them into experience. Turn them into knowledge. Turn them into
                relationships. Turn them into opportunities. And then use those opportunities to
                build something you're proud of."
              </p>
              <div className="mono pt-2 text-[11px] tracking-widest text-white">
                — {FOUNDERS.names.toUpperCase()}
                <br />
                <span className="text-muted-foreground">FOUNDERS, SMASH LEMONADE</span>
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* SECTION 9 — CTA */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 photo-band opacity-25"
          style={{ backgroundImage: `url(${BRAND.photos.lemonSplash})` }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/88" />
        <Section testId="founders-cta" className="relative text-center">
          <h2 className="neon text-4xl leading-tight sm:text-5xl lg:text-6xl">
            Ready to smash <span className="text-primary">your next opportunity?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">
            If you're ready to learn the Smash Lemonade system and develop the skills required to
            operate your own pop-up, Smash Academy is where your journey begins.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to={user ? "/academy" : "/login"}
              data-testid="founders-cta-academy"
              className="display group inline-flex items-center gap-2 rounded-sm bg-primary px-7 py-4 text-xl text-primary-foreground transition-colors hover:bg-[#E6C800]"
            >
              Enter Smash Academy
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/apply"
              data-testid="founders-cta-operator"
              className="display inline-flex items-center gap-2 rounded-sm border border-primary px-7 py-4 text-xl text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              Learn About Becoming An Operator
            </Link>
          </div>
          <p className="mono mt-8 text-[11px] tracking-[0.3em] text-muted-foreground">
            LEARN THE SYSTEM. BUILD THE MINDSET. CREATE THE OPPORTUNITY.
          </p>
        </Section>
      </div>
    </div>
  );
}
